import { ColumnDef, Row } from '@tanstack/react-table'
import { useMemo } from 'react'
import { EditOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import {
  IconButtonCell,
  TextCell,
  UserCardCell,
  UserCell,
} from '@pages/Admin/Reports/Payments/components/Cells/Cells'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import { IGroupAllLessonItem } from '../../utils/types'
import { useFranchises } from '@/hooks/useFranchises'

interface TableColumnsProps {
  onEdit: (report: IGroupAllLessonItem) => void
  onSeminarianClick: (user: IUser) => void
}

const useGroupAllLessonsColumns = ({
  onEdit,
  onSeminarianClick,
}: TableColumnsProps) => {
  const settings = useSelector((store: any) => ({
    telebosEnabled: store.settings?.isTelebosIntegrationEnabled === '1',
    teacherCanEdit: store.settings?.isTeacherCanEditStatuses === '1',
    role: store.profile?.profile?.role,
  }))
  const { enabled: franchisesEnabled } = useFranchises()

  return useMemo<ColumnDef<IGroupAllLessonItem>[]>(() => {
    const isTeacher = ['seminarian', 'senior-seminarian'].includes(
      settings.role,
    )
    const canEdit = !isTeacher || settings.teacherCanEdit

    const allColumns: ColumnDef<IGroupAllLessonItem>[] = [
      {
        header: '',
        id: 'index',
        size: 40,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell
            text={String(row.original.index ?? row.index + 1)}
            styles={{ textAlign: 'center' }}
          />
        ),
      },
      {
        header: 'Дата и время занятия',
        id: 'date-time',
        size: 150,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell text={row.original.date_time ?? '-'} />
        ),
      },
      {
        header: 'Педагог',
        id: 'seminarian',
        size: 180,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <UserCardCell
            user={row.original.seminarian}
            onClick={() => onSeminarianClick(row.original.seminarian)}
          />
        ),
      },
      {
        header: 'Курс',
        id: 'course',
        size: 180,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell text={row.original.course_name ?? '-'} />
        ),
      },
      {
        header: 'Группы',
        id: 'group',
        size: 200,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell
            text={
              row.original.groups?.length
                ? row.original.groups
                    .map((group) => `${group.name} — ${group.student_count}`)
                    .join('\n')
                : row.original.group?.name ?? '-'
            }
            styles={{ whiteSpace: 'pre-line', display: 'block', overflow: 'visible' }}
          />
        ),
      },
      {
        header: 'Филиал',
        id: 'franchise',
        size: 180,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell
            text={
              row.original.groups?.length
                ? Array.from(
                    new Set(
                      row.original.groups
                        .map((group) => group.franchise?.name)
                        .filter(Boolean),
                    ),
                  ).join(', ') || '-'
                : row.original.franchise?.name ?? '-'
            }
          />
        ),
      },
      {
        header: 'Кол-во учащихся',
        id: 'students-count',
        size: 100,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell
            text={String(
              row.original.student_count ?? row.original.students_count ?? '-',
            )}
          />
        ),
      },
      {
        header: 'Длительность',
        id: 'duration',
        size: 90,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell text={String(row.original.duration ?? '-')} />
        ),
      },
      {
        header: 'Время захода пед.',
        id: 'telebos-join-time',
        size: 150,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell
            text={
              row.original.telebos_teacher_joined_at_formatted ?? 'Не проведено'
            }
          />
        ),
      },
      {
        header: 'Опоздание (мин)',
        id: 'telebos-late-minutes',
        size: 90,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell
            text={row.original.telebos_late_minutes?.toString() ?? '-'}
          />
        ),
      },
      {
        header: 'Длит. ур.',
        id: 'telebos-duration',
        size: 90,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell
            text={row.original.telebos_lesson_duration?.toString() ?? '-'}
          />
        ),
      },
      {
        header: 'Статус',
        id: 'status',
        size: 120,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell
            text={row.original.status_text ?? '-'}
            wrapperStyles={{
              backgroundColor: getStatusColor(row.original.status),
            }}
          />
        ),
      },
      {
        header: 'Комментарий',
        id: 'comment',
        size: 180,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TooltipWrapper title={row.original.comment ?? ''}>
            <TextCell text={row.original.comment ?? '-'} />
          </TooltipWrapper>
        ),
      },
      {
        header: 'ЗП',
        id: 'salary',
        size: 100,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <TextCell text={String(row.original.salary ?? '-')} />
        ),
      },
      {
        header: '',
        id: 'edit',
        size: 50,
        cell: ({ row }: { row: Row<IGroupAllLessonItem> }) => (
          <IconButtonCell
            icon={
              <EditOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            }
            onClick={() => onEdit(row.original)}
          />
        ),
      },
    ]

    return allColumns.filter((item) => {
      if (item.id === 'franchise' && !franchisesEnabled) {
        return false
      }
      if (
        [
          'telebos-join-time',
          'telebos-late-minutes',
          'telebos-duration',
        ].includes(item.id as string)
      ) {
        return settings.telebosEnabled
      }
      if (item.id === 'edit') {
        return canEdit
      }
      return true
    })
  }, [settings, onEdit, onSeminarianClick, franchisesEnabled])
}

const getStatusColor = (status: number) => {
  switch (status) {
    case 5:
      return 'var(--color-status-success-border)'
    case 10:
    case 11:
      return 'var(--color-status-danger-border)'
    case 12:
      return 'var(--color-status-danger-bg)'
    case 9:
      return 'var(--color-status-warning)'
    default:
      return 'var(--color-status-warning-border)'
  }
}

export default useGroupAllLessonsColumns
